"use client"

import * as React from "react"
import { HugeiconsIcon } from "@hugeicons/react"
import { ArrowLeft01Icon, ArrowRight01Icon } from "@hugeicons-pro/core-stroke-rounded"
import { DayPicker } from "react-day-picker"

import { cn } from "@/lib/utils"
import { buttonVariants } from "@/components/ui/button"

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  defaultMonth: propDefaultMonth,
  ...props
}: React.ComponentProps<typeof DayPicker>) {
  // Determine the default month based on selected date
  const getDefaultMonth = (): Date | undefined => {
    // If defaultMonth is explicitly provided, use it
    if (propDefaultMonth) {
      return propDefaultMonth
    }
    
    // Otherwise, derive from selected prop
    const selected = props.selected
    if (selected) {
      if (selected instanceof Date) {
        // Single date mode
        return selected
      } else if (Array.isArray(selected) && selected.length > 0) {
        // Multiple dates mode - use first selected date
        const firstDate = selected[0]
        if (firstDate instanceof Date) {
          return firstDate
        }
      } else if (typeof selected === 'object' && selected !== null && 'from' in selected) {
        // Range mode - use the 'from' date
        const range = selected as { from?: Date }
        if (range.from instanceof Date) {
          return range.from
        }
      }
    }
    
    // Default to current month
    return undefined
  }

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-3", className)}
      defaultMonth={getDefaultMonth()}
      formatters={{
        formatWeekdayName: (date) => {
          const weekdays = ['S', 'M', 'T', 'W', 'T', 'F', 'S']
          return weekdays[date.getDay()]
        },
      }}
      classNames={{
        months: "flex flex-col sm:flex-row gap-2",
        month: "flex flex-col gap-4",
        caption: "flex justify-center py-2 relative items-center w-full",
        caption_label: "text-base font-medium text-atrium-black",
        nav: "flex items-center gap-1",
        nav_button: cn(
          buttonVariants({ variant: "outline" }),
          "size-9 rounded-full bg-transparent p-0 text-atrium-black text-atrium-black hover:bg-atrium-content border-2 border-transparent hover:border-atrium-content focus:border-accent-700 focus-visible:border-accent-700 focus:ring-2 focus-visible:ring-2 focus-visible:ring-accent-700/20 focus:ring-accent-700/20"
        ),
        nav_button_previous: "absolute left-1",
        nav_button_next: "absolute right-1",
        table: "w-full border-collapse space-x-1",
        head_row: "flex",
        head_cell:
          "text-atrium-black rounded-md w-12 font-normal text-[0.8rem]",
        row: "flex w-full mt-2",
        cell: cn(
          "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected].day-range-end)]:rounded-r-md",
          props.mode === "range"
            ? "[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md"
            : "[&:has([aria-selected])]:rounded-md"
        ),
        day: cn(
          buttonVariants({ variant: "ghost" }),
          "size-12 rounded-full p-0 font-normal aria-selected:opacity-100 aria-selected:hover:bg-accent-700 text-atrium-black hover:border-accent-700 border-2 border-transparent font-medium text-sm disabled:opacity-20 focus-visible:border-accent-700 focus-visible:ring-accent-700/20"
        ),
        day_range_start:
          "day-range-start aria-selected:bg-accent-600 aria-selected:text-white",
        day_range_end:
          "day-range-end aria-selected:bg-accent-600 aria-selected:text-white",
        day_today: "",
        day_outside:
          "day-outside text-atrium-black",
        day_disabled: "text-atrium-black opacity-50",
        day_range_middle:
          "aria-selected:bg-gray-100 aria-selected:text-black",
        day_hidden: "invisible",
        day_selected:
          "bg-accent-700 text-white hover:bg-accent-700 focus:bg-accent-700",
        ...classNames,
      }}
      components={{
        IconLeft: ({ className, ...props }) => (
          <HugeiconsIcon icon={ArrowLeft01Icon} className={cn("size-4", className)} />
        ),
        IconRight: ({ className, ...props }) => (
          <HugeiconsIcon icon={ArrowRight01Icon} className={cn("size-4", className)} />
        ),
      }}
      {...props}
    />
  )
}

export { Calendar }
